import styled from "@emotion/styled/macro";
import {
  Button,
  LoadingIndicator,
  Modal,
  SvgIcon,
  SvgNames,
  tokens,
  Typography,
} from "@sunrun/experience-ui-components";
import SunrunLogo from "../assets/images/sunrun-logo.svg";
import {
  ListOffer,
  ReviewOffer,
} from "../../../amplify/backend/function/offerexpstoreFrontApi/ts/public/offerTypes";
import headerBg from "assets/images/header-bg.png";
import { ClassicViewPillTooltip } from "components/molecules/ClassicViewPillTooltip";
import { DesignRender } from "components/organisms/DesignRender";
import { maxWidth } from "components/atoms/GlobalStyles";
import { useOfferReviewSimplifiedIneligibleReason } from "hooks/useOfferReviewSimplifiedIneligibleReason";

type OfferReviewHeaderProps = {
  nonSolarView: boolean;
  offer: ReviewOffer;
  prospectId: string;
  address: string;
  isMobile: boolean;
  isTablet: boolean;
  isOffersLoading: boolean;
  offers: ListOffer[] | undefined;
  cognitoToken: string | undefined;
  isCustomer: boolean;
  heroTitle: string;
  onExitClick: () => void;
  navigateToOfferSelector: () => void;
  onCheckoutClick: () => void;
  checkoutDisabled?: boolean;
  showMapModalOpen: boolean;
  setShowMapModalOpen: (show: boolean) => void;
};

const SunrunLogoWithBreezeTooltip = ({
  offer,
  offers,
  isCustomer,
}: {
  offer: ReviewOffer;
  offers: ListOffer[] | undefined;
  isCustomer: boolean;
}) => {
  const { reason, breezeCreatedDateCutoff } =
    useOfferReviewSimplifiedIneligibleReason({
      offer,
      offers,
      isCustomer,
    });
  return (
    <LogoColumn>
      <LogoImage src={SunrunLogo} alt="Sunrun Logo" />
      {!isCustomer && reason !== null && (
        <ClassicViewPillTooltip
          reason={reason}
          breezeCreatedDateCutoff={breezeCreatedDateCutoff}
        />
      )}
    </LogoColumn>
  );
};

const NewLayoutHeader = ({
  offer,
  address,
  isMobile,
  isOffersLoading,
  offers,
  cognitoToken,
  isCustomer,
  onExitClick,
  navigateToOfferSelector,
}: OfferReviewHeaderProps) => (
  <NewHeader isMobile={isMobile}>
    <HeaderContainer>
      <HeaderContent isMobile={isMobile}>
        {isMobile ? (
          <>
            <HeaderUpRow>
              {isOffersLoading && <LoadingIndicator />}
              {!isOffersLoading &&
                offers &&
                offers.length > 0 &&
                cognitoToken && (
                  <SelectContainer>
                    <Button
                      style={{
                        padding: isMobile ? "8px 16px" : "4px 16px",
                        border: "1px solid white",
                      }}
                      onClick={onExitClick}
                      size="sm"
                      iconLeft={
                        <SvgIcon
                          name={SvgNames.ChevronLeft}
                          width="20px"
                          color="white"
                        />
                      }
                    >
                      All Offers
                    </Button>
                  </SelectContainer>
                )}
              {isCustomer &&
                !isOffersLoading &&
                offers &&
                offers.length > 0 && (
                  <WhiteButton
                    size="sm"
                    color="secondary"
                    onClick={() => navigateToOfferSelector()}
                    iconLeft={
                      <SvgIcon
                        name={SvgNames.ChevronLeft}
                        width="20px"
                        color="white"
                      />
                    }
                  >
                    All Offers
                  </WhiteButton>
                )}
              <SunrunLogoWithBreezeTooltip
                offer={offer}
                offers={offers}
                isCustomer={isCustomer}
              />
            </HeaderUpRow>
            <HeaderDownRow>
              <AddressWrapper>
                <Typography variant="h2" align="center" color={tokens.WHITE}>
                  {offer.customerName}
                </Typography>
                <Typography variant="p" align="center" color={tokens.WHITE}>
                  {address}
                </Typography>
              </AddressWrapper>
            </HeaderDownRow>
          </>
        ) : (
          <>
            {isOffersLoading && <LoadingIndicator />}
            {!isOffersLoading &&
              offers &&
              offers.length > 0 &&
              cognitoToken && (
                <SelectContainer>
                  <Button
                    style={{
                      padding: isMobile ? "8px 16px" : "4px 16px",
                      border: "1px solid white",
                    }}
                    onClick={onExitClick}
                    size="sm"
                    iconLeft={
                      <SvgIcon
                        name={SvgNames.ChevronLeft}
                        width="20px"
                        color="white"
                      />
                    }
                  >
                    All Offers
                  </Button>
                </SelectContainer>
              )}
            {isCustomer && !isOffersLoading && offers && offers.length > 0 && (
              <WhiteButton
                size="sm"
                color="secondary"
                onClick={() => navigateToOfferSelector()}
                iconLeft={
                  <SvgIcon
                    name={SvgNames.ChevronLeft}
                    width="20px"
                    color="white"
                  />
                }
              >
                All Offers
              </WhiteButton>
            )}
            <AddressWrapper>
              <Typography variant="h2" align="center" color={tokens.WHITE}>
                {offer.customerName}
              </Typography>
              <Typography variant="p" align="center" color={tokens.WHITE}>
                {address}
              </Typography>
            </AddressWrapper>
            <SunrunLogoWithBreezeTooltip
              offer={offer}
              offers={offers}
              isCustomer={isCustomer}
            />
          </>
        )}
      </HeaderContent>
    </HeaderContainer>
  </NewHeader>
);

const OfferReviewHeader = (props: OfferReviewHeaderProps) => {
  const { showMapModalOpen, setShowMapModalOpen, offer } = props;

  return (
    <>
      {showMapModalOpen && (
        <SiteModelModal
          onClose={() => setShowMapModalOpen(false)}
          testId="review-page-site-model-modal"
        >
          <SiteModelContainer>
            <DesignRender
              designId={offer?.designId}
              designVersion={offer?.designVersion}
              showTreeRemovalLegend={offer?.treeRemovalRequired}
            />
          </SiteModelContainer>
        </SiteModelModal>
      )}
      <NewLayoutHeader {...props} />
    </>
  );
};

const SelectContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  @media (max-width: ${maxWidth}px) {
    justify-content: space-between;
  }
`;

const WhiteButton = styled(Button)`
  color: ${tokens.WHITE};
  border-color: ${tokens.WHITE};
`;

const NewHeader = styled.div<{ isMobile: boolean }>`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  background: url(${headerBg});
  height: 172px;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  padding-inline: 64px;
  position: relative;
  ${({ isMobile }) =>
    isMobile &&
    `
    padding: 24px 16px;
    height: fit-content;
  `}
`;

const HeaderContainer = styled.div`
  max-width: 1005px;
  width: 100%;
  margin: 0 auto;
  display: flex;
  justify-content: flex-end;
`;

const HeaderUpRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;
const HeaderDownRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
`;

const HeaderContent = styled.div<{ isMobile: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;

  ${({ isMobile }) =>
    isMobile &&
    `
    flex-direction: column;
    gap: 28px;
  `}
`;

const LogoColumn = styled.span`
  display: inline-flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
`;

const LogoImage = styled.img`
  width: 130px;

  @media (max-width: 768px) {
    width: 84px;
  }
`;

const AddressWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const SiteModelModal = styled(Modal)`
  > .modal-container {
    height: 90%;
    width: 90%;
    max-height: 98vh;
    overflow: hidden;

    > .modal-content-container {
      padding-top: 0px;
      overflow: hidden;
    }

    > #modal-header {
      padding-bottom: 0px;
    }
  }
`;

const SiteModelContainer = styled.div`
  position: relative;
  width: 100%;
  margin: auto;
  padding-top: 10px;
  text-align: center;
`;

export { OfferReviewHeader };
